import { jsx, jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  AdvancedOptions,
  CheckboxField,
  DropdownButton,
  generateField,
  RadioGroupField
} from "@teselagen/ui";
import {
  filterSequenceString,
  getReverseComplementSequenceString,
  calculatePercentGC,
  calculateEndStability
} from "@teselagen/sequence-utils";
import AddOrEditAnnotationDialog from "../AddOrEditAnnotationDialog";
import { convertRangeTo0Based } from "@teselagen/range-utils";
import classNames from "classnames";
import "./style.css";
import { getSequenceWithinRange } from "@teselagen/range-utils";
import { flatMap } from "lodash-es";
import CaretPositioning, {
  selectionSaveCaretPosition
} from "./EditCaretPosition";
import { Menu, MenuItem } from "@blueprintjs/core";
import MeltingTemp from "../../StatusBar/MeltingTemp";
import { getStructuredBases } from "../../RowItem/StackedAnnotations/getStructuredBases";
const CustomContentEditable = generateField(function CustomContentEditable2({
  input,
  disabled,
  sequenceData,
  sequenceLength,
  start,
  end,
  primerBindsOn,
  forward
}) {
  const bases = input.value;
  const [hasTempError, setTempError] = useState(false);
  const inputRef = useRef(null);
  const [caretPosition, setCaretPosition] = useState({ start: 0, end: 0 });
  const emitChange = (e) => {
    const newVal = e.target.innerText;
    const savedCaretPosition = CaretPositioning.saveSelection(e.currentTarget);
    setCaretPosition(savedCaretPosition);
    const [newBases, warnings] = filterSequenceString(newVal, sequenceData);
    if (warnings.length) {
      setTempError(true);
      setTimeout(() => {
        setTempError(false);
      }, 200);
    }
    const restore = selectionSaveCaretPosition(inputRef.current);
    input.onChange(newBases || "");
    setTimeout(() => {
      restore();
    }, 0);
  };
  useEffect(() => {
    CaretPositioning.restoreSelection(inputRef.current, caretPosition);
  }, [bases]);
  const basesToUse = bases || "";
  const { allBasesWithMetaData } = getStructuredBases({
    annotationRange: { start: start - 1, end: end - 1 },
    forward,
    bases: basesToUse,
    start: start - 1,
    end: end - 1,
    fullSequence: sequenceData.sequence,
    primerBindsOn,
    sequenceLength
  });
  let html = flatMap(
    allBasesWithMetaData,
    ({ b, isMatch, isAmbiguousMatch }) => {
      if (b === "&") return [];
      return `<span class="${isMatch ? "" : isAmbiguousMatch ? "tg-ambiguous-match-seq" : "tg-no-match-seq"}">${b}</span>`;
    }
  );
  html = html.join("");
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        display: "flex",
        ...disabled ? { pointerEvents: "none" } : {}
      },
      children: [
        /* @__PURE__ */ jsx(
          "span",
          {
            style: {
              verticalAlign: "top",
              marginTop: 9,
              marginRight: 3,
              fontSize: 12,
              color: "grey"
            },
            children: "5'"
          }
        ),
        /* @__PURE__ */ jsx(
          "span",
          {
            ref: inputRef,
            spellCheck: "false",
            contentEditable: !disabled,
            className: classNames("bp3-input tg-custom-sequence-editable", {
              hasTempError
            }),
            onInput: emitChange,
            dangerouslySetInnerHTML: { __html: html }
          }
        ),
        /* @__PURE__ */ jsx(
          "span",
          {
            style: {
              alignSelf: "end",
              marginBottom: 9,
              marginLeft: 3,
              fontSize: 12,
              color: "grey"
            },
            children: "3'"
          }
        )
      ]
    }
  );
});
const RenderBases = (props) => {
  const {
    sequenceData,
    readOnly,
    start,
    end,
    linkedOligo,
    getLinkedOligoLink,
    useLinkedOligo,
    sequenceLength,
    primerBindsOn,
    bases,
    forward,
    defaultLinkedOligoMessage,
    change
  } = props;
  let defaultValue;
  const seqLen = sequenceData.sequence.length;
  const validate = useMemo(() => {
    return (val) => {
      if (!val) return;
      if (val.length > seqLen) {
        return "Primer cannot be longer than sequence";
      }
      return;
    };
  }, [seqLen]);
  const normalizedSelection = convertRangeTo0Based({
    start,
    end
  });
  if (!bases) {
    let bps = getSequenceWithinRange(
      normalizedSelection,
      sequenceData.sequence
    );
    if (!forward) {
      bps = getReverseComplementSequenceString(bps);
    }
    defaultValue = bps;
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        borderTop: "1px solid #A7B6C2",
        borderBottom: "1px solid #A7B6C2"
      },
      children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            },
            children: [
              /* @__PURE__ */ jsx(
                CheckboxField,
                {
                  name: "useLinkedOligo",
                  label: "Linked Oligo?",
                  tooltipInfo: `Check this box to link this primer to an oligo in your Oligo Library. If the primer bases match exactly the bases of an existing oligo, it will be linked to that existing oligo. If the bases don't match, a new oligo will be created in the library.`,
                  noMarginBottom: true,
                  defaultValue: useLinkedOligo ?? true,
                  disabled: readOnly
                }
              ),
              useLinkedOligo && /* @__PURE__ */ jsx(
                "div",
                {
                  className: "tg-linked-oligo-holder",
                  style: { marginTop: -5, fontStyle: "italic", fontSize: 11 },
                  children: getLinkedOligoLink && getLinkedOligoLink(props) || linkedOligo || (defaultLinkedOligoMessage !== void 0 ? defaultLinkedOligoMessage : "Will Be Created On Save")
                }
              )
            ]
          }
        ),
        useLinkedOligo && /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx(
            CustomContentEditable,
            {
              showErrorIfUntouched: true,
              tooltipError: true,
              primerBindsOn,
              validate,
              sequenceLength,
              disabled: readOnly,
              ...props,
              defaultValue: bases ?? defaultValue,
              name: "bases",
              label: /* @__PURE__ */ jsxs("div", { className: "tg-bases-label", children: [
                /* @__PURE__ */ jsxs("div", { style: { display: "flex" }, children: [
                  "Bases",
                  " ",
                  /* @__PURE__ */ jsxs("div", { style: { fontSize: 10 }, children: [
                    " ",
                    "\xA0 (Length: ",
                    bases ? bases.length : 0,
                    ")"
                  ] })
                ] }),
                /* @__PURE__ */ jsx("div", { style: { width: "fit-content" }, children: /* @__PURE__ */ jsx(
                  DropdownButton,
                  {
                    disabled: readOnly,
                    intent: "primary",
                    small: true,
                    menu: /* @__PURE__ */ jsxs(Menu, { children: [
                      /* @__PURE__ */ jsx(
                        MenuItem,
                        {
                          onClick: () => {
                            change("forward", true);
                            change(
                              "bases",
                              getSequenceWithinRange(
                                normalizedSelection,
                                sequenceData.sequence
                              )
                            );
                          },
                          text: "Forward"
                        },
                        "forward"
                      ),
                      /* @__PURE__ */ jsx(
                        MenuItem,
                        {
                          onClick: () => {
                            change("forward", false);
                            change(
                              "bases",
                              getReverseComplementSequenceString(
                                getSequenceWithinRange(
                                  normalizedSelection,
                                  sequenceData.sequence
                                )
                              )
                            );
                          },
                          text: "Reverse"
                        },
                        "reverse"
                      )
                    ] }),
                    children: "Set From Selection"
                  }
                ) })
              ] })
            }
          ),
          /* @__PURE__ */ jsx(AdvancedOptions, { style: { marginBottom: 10 }, children: /* @__PURE__ */ jsx(
            RadioGroupField,
            {
              name: "primerBindsOn",
              inline: true,
              inlineLabel: true,
              disabled: readOnly,
              label: "Oligo Binds On",
              tooltipError: true,
              options: [
                { label: "5' End", value: "5prime" },
                { label: "3' End", value: "3prime" }
              ]
            }
          ) }),
          /* @__PURE__ */ jsx(
            MeltingTemp,
            {
              InnerWrapper: TextInnerWrapper,
              sequence: bases
            }
          ),
          /* @__PURE__ */ jsxs(TextInnerWrapper, { children: [
            "GC content: ",
            bases && calculatePercentGC(bases).toFixed(1),
            "%"
          ] }),
          /* @__PURE__ */ jsxs(TextInnerWrapper, { children: [
            "3' Stability: ",
            bases && calculateEndStability(bases),
            " kcal/mol"
          ] })
        ] })
      ]
    }
  );
};
const TextInnerWrapper = (p) => /* @__PURE__ */ jsx(
  "div",
  {
    className: "bp3-text-muted bp3-text-small",
    style: {
      marginBottom: 15,
      marginTop: -5,
      fontStyle: "italic"
    },
    children: p.children
  }
);
export default AddOrEditAnnotationDialog({
  formName: "AddOrEditPrimerDialog",
  getProps: (props) => ({
    upsertAnnotation: props.upsertPrimer,
    annotationTypePlural: "primers",
    RenderBases
  })
});
